import { useCallback, useEffect, useRef } from 'react';

import { A2AClient } from '@a2a-js/sdk/client';

import { organizationHeader } from '@cast/constants';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const transformUrlToApiPath = (url: string): string => {
  if (url.startsWith('/')) {
    return url;
  }
  try {
    const urlObj = new URL(url);
    return `/api${urlObj.pathname}${urlObj.search}`;
  } catch {
    return url;
  }
};

const createFetchWithCredentials = (
  organizationId?: string,
  getSignal?: () => AbortSignal | undefined
): typeof fetch => {
  return async (url, init) => {
    const transformedUrl = transformUrlToApiPath(url as string);
    const headers = new Headers(init?.headers);
    headers.set(organizationHeader, organizationId || '');
    return fetch(transformedUrl, {
      ...init,
      headers,
      credentials: 'include' as RequestCredentials,
      signal: getSignal?.() || init?.signal,
    });
  };
};

export const useA2AClient = (onError: (msg: string) => void) => {
  const clientRef = useRef<A2AClient>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const { currentOrganization } = useOrganizations();

  const abortInFlight = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Clear stale client immediately so in-flight sends fail fast on org change
    clientRef.current = undefined;
    const orgId = currentOrganization?.id;

    const fetchWithCredentials = createFetchWithCredentials(
      orgId,
      () => abortControllerRef.current?.signal
    );

    let cancelled = false;

    A2AClient.fromCardUrl(
      '/api/copilot/orchestrator/.well-known/agent-card.json',
      { fetchImpl: fetchWithCredentials }
    )
      .then((client) => {
        if (!cancelled) clientRef.current = client;
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to initialise A2A client, error: ', err);
        onErrorRef.current(
          `⚠️ Encountered an error while initialising OpsPilot assistant.`
        );
      });

    return () => {
      cancelled = true;
      clientRef.current = undefined;
    };
  }, [currentOrganization?.id]);

  useEffect(() => abortInFlight, [abortInFlight]);

  const startNewController = () => {
    abortInFlight();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  };

  return { clientRef, abortInFlight, startNewController };
};
